/**
 * Base API Service
 * Provides common functionality for all API services
 */
import { apiRequest } from './apiInterceptor';

/**
 * Creates query string from parameters object
 * @param {Object} params - Object containing query parameters
 * @returns {string} - Formatted query string
 */
export const createQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';
  
  const queryParams = [];
  
  for (const [key, value] of Object.entries(params)) {
    // Skip null or undefined values
    if (value === null || value === undefined) continue;
    
    // Handle arrays
    if (Array.isArray(value)) {
      value.forEach(item => {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
      });
    } else {
      queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  
  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
};

/**
 * Base class for all API services
 */
export class ApiService {  /**
   * Makes a GET request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise<any>} - Response data
   */
  async get(endpoint, params = {}, caller = 'ApiService.get') {
    const queryString = createQueryString(params);
    const fullUrl = `${endpoint}${queryString}`;
    console.log('[apiService.get] Calling apiRequest with URL:', fullUrl, 'caller:', caller);
    return apiRequest(fullUrl, { method: 'GET', caller });
  }
  
  /**
   * Makes a POST request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @returns {Promise<any>} - Response data
   */
  async post(endpoint, data) {
    return apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
  
  /**
   * Makes a PUT request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @returns {Promise<any>} - Response data
   */
  async put(endpoint, data) {
    return apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
  
  /**
   * Makes a DELETE request to the API
   * @param {string} endpoint - API endpoint
   * @returns {Promise<any>} - Response data
   */
  async delete(endpoint) {
    return apiRequest(endpoint, {
      method: 'DELETE'
    });
  }
}

// Export singleton instance
export default new ApiService();
